import React, { useEffect, useState, useMemo } from "react";
import { api, API } from "@/lib/api";
import { useClients } from "@/contexts/ClientContext";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, ReferenceLine,
} from "recharts";
import { ArrowUpRight, TrendingUp, Database, CalendarRange, Sparkles,
         CheckCircle2, Clock, AlertCircle, Globe2, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import ChartLegend, { useToggleableSeries } from "@/components/ChartLegend";
import ChartDateRange, { useChartDateRange } from "@/components/ChartDateRange";
import EcosystemDiagram from "@/components/EcosystemDiagram";
import PerPropertyTrends from "@/components/PerPropertyTrends";

// Stable Recharts config refs hoisted to module scope so we don't
// reallocate them on every render (and re-trigger chart re-renders).
const TOOLTIP_STYLE = { borderRadius: 2, border: "1px solid #E4E4E7" };
const TOOLTIP_STYLE_SM = { borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 };
const LINE_DOT = { r: 3 };
const LINE_ACTIVE_DOT = { r: 5 };
const BAR_RADIUS = [3, 3, 0, 0];
const BAR_MARGIN = { top: 16, right: 16, left: 0, bottom: 0 };


const platformLabels = {
  meta: "Meta", facebook: "Facebook", instagram: "Instagram",
  google_ads: "Google Ads", youtube: "YouTube", x_twitter: "X (Twitter)",
  linkedin: "LinkedIn", pinterest: "Pinterest", tiktok: "TikTok", awstats: "AWStats",
  google_analytics: "Google Analytics", google_search_console: "Google Search Console",
  bing_ads: "Bing Ads", bing_analytics: "Bing Analytics",
  worklogs: "Worklogs",
};

const COLORS = ["#002FA7", "#10B981", "#FFC107", "#E53935", "#0EA5E9", "#A855F7", "#F97316", "#14B8A6"];

export default function Overview() {
  const { selected, clients } = useClients();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logoOk, setLogoOk] = useState(true);
  const lineToggle = useToggleableSeries();
  // Master date range for the whole page — defaults to last 12 months as requested.
  const masterRange = useChartDateRange("last12");

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    setLogoOk(true);
    api.get(`/clients/${selected.client_id}/dashboard`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selected?.client_id]);

  // Per-client integration status — drives the "Data sources" strip below the
  // KPIs. Each fetch is independent + non-blocking so a single 4xx doesn't
  // tank the whole strip.
  const [integrations, setIntegrations] = useState({
    google: { loading: true }, openai_ads: { loading: true }, linkedin: { loading: true },
  });
  useEffect(() => {
    if (!selected) return;
    setIntegrations({
      google: { loading: true }, openai_ads: { loading: true }, linkedin: { loading: true },
    });
    const cid = selected.client_id;
    api.get(`/clients/${cid}/google-feeds`)
      .then(({ data: d }) => {
        const counts = d?.feeds || {};
        const total = Object.values(counts).reduce(
          (n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0,
        );
        setIntegrations((s) => ({ ...s, google: { connected: total > 0, total } }));
      })
      .catch(() => setIntegrations((s) => ({ ...s, google: { connected: false } })));
    api.get(`/clients/${cid}/openai-ads/connection`)
      .then(({ data: d }) => setIntegrations(
        (s) => ({ ...s, openai_ads: { connected: !!d?.connected, meta: d } })))
      .catch(() => setIntegrations((s) => ({ ...s, openai_ads: { connected: false } })));
    api.get(`/clients/${cid}/linkedin/connection`)
      .then(({ data: d }) => setIntegrations(
        (s) => ({ ...s, linkedin: { connected: !!d?.connected,
                                     mdp_approved: !!d?.mdp_approved, meta: d } })))
      .catch(() => setIntegrations((s) => ({ ...s, linkedin: { connected: false } })));
  }, [selected?.client_id]);

  const kpis = useMemo(() => {
    if (!data) return [];
    // Build a fast lookup of months WITHIN the master range
    const monthsInRange = new Set(
      (data.months || []).filter((m) => masterRange.inRange(m))
    );
    // Sum metric values across platforms — but ONLY for rows whose period_month
    // falls within the master date range.
    const wanted = ["impressions", "clicks", "spend", "cost", "conversions",
                    "sessions", "pageviews", "engagement", "video_views", "reach", "hours"];
    const found = {};
    Object.entries(data.by_platform || {}).forEach(([plat, rows]) => {
      (rows || []).forEach((row) => {
        if (!monthsInRange.has(row.period_month)) return;
        Object.entries(row.totals || {}).forEach(([metric, val]) => {
          const slug = (metric || "").toLowerCase().replace(/\s+/g, "_");
          for (const w of wanted) {
            if (slug.includes(w)) {
              const name = w.replace(/_/g, " ");
              if (!found[name]) found[name] = { total: 0, platforms: new Set() };
              found[name].total += Number(val || 0);
              found[name].platforms.add(plat);
            }
          }
        });
      });
    });
    return Object.entries(found).slice(0, 4).map(([name, v]) => ({
      name, total: v.total, platforms: [...v.platforms].length,
    }));
  }, [data, masterRange]);

  // Build a multi-line chart: months x platform total impressions or row count
  const chartData = useMemo(() => {
    if (!data) return [];
    const months = data.months || [];
    return months.map((m) => {
      const row = { month: m };
      Object.entries(data.by_platform || {}).forEach(([plat, arr]) => {
        const hit = arr.find((x) => x.period_month === m);
        if (hit) {
          // primary numeric — pick the largest total
          const totals = hit.totals || {};
          const max = Object.values(totals).reduce((a, b) => Math.max(a, Number(b || 0)), 0) || hit.row_count;
          row[platformLabels[plat] || plat] = max;
        }
      });
      return row;
    });
  }, [data]);

  const platformsInChart = useMemo(() => {
    if (!data) return [];
    return Object.keys(data.by_platform || {}).map((p) => platformLabels[p] || p);
  }, [data]);

  // Months in the master range — used to filter the "Months of data" KPI tile
  // and to bound the per-platform "last value" tiles below.
  const monthsInRange = useMemo(() => {
    return (data?.months || []).filter((m) => masterRange.inRange(m));
  }, [data, masterRange]);

  if (!clients?.length) {
    return (
      <EmptyState
        title="No clients yet"
        body="Create your first client company to start uploading marketing data and generating monthly reports."
        ctaTo="/clients"
        ctaLabel="Add a client"
      />
    );
  }

  if (!selected) return null;

  return (
    <div className="space-y-8" data-testid="overview-page">
      {/* "Ask the AI strategist" CTA — the client/logo identity already lives in
          ClientDetailLayout so we don't repeat it here. */}
      <div className="flex justify-end">
        <Link to="/ai"
          className="hidden md:inline-flex items-center gap-2 text-sm px-4 py-2.5 bg-[#002FA7] text-white hover:bg-[#00227A] rounded-sm transition-colors"
          data-testid="overview-ai-cta"
        >
          <Sparkles className="h-4 w-4" /> Ask the AI strategist
        </Link>
      </div>

      {/* Marketing ecosystem diagram — central hub = each property; satellite
          tiles show active vs missing channels so the client immediately
          sees recommended next-best channels (e.g. TikTok). */}
      <EcosystemDiagram clientId={selected.client_id} />

      {/* Master date range — controls every metric / chart / per-property card on this page */}
      <Card className="rounded-sm border-zinc-200 bg-zinc-50" data-testid="overview-master-range">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 grid place-items-center rounded-sm bg-[#002FA7] text-white">
              <CalendarRange className="h-4 w-4" />
            </div>
            <div>
              <div className="label-overline">Master date range</div>
              <div className="text-xs text-zinc-600 mt-0.5">
                Filters every KPI, chart and per-property trend on this page. Default is the last 12 months.
              </div>
            </div>
          </div>
          <ChartDateRange
            {...masterRange}
            actualRange={(() => {
              const sorted = (data?.months || []).slice().sort();
              return { startYM: sorted[0], endYM: sorted[sorted.length - 1] };
            })()}
            testIdPrefix="overview-master-range"
          />
        </CardContent>
      </Card>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile label="Platforms tracked" value={data?.platforms?.length || 0} icon={Database} loading={loading} />
        <KpiTile label="Months in range" value={monthsInRange.length} icon={CalendarRange} loading={loading} />
        {kpis.slice(0, 2).map((k) => (
          <KpiTile key={k.name} label={k.name} value={formatBig(k.total)} icon={TrendingUp} loading={loading} />
        ))}
        {kpis.length < 2 && Array.from({ length: 2 - kpis.length }).map((_, i) => (
          <KpiTile key={`ph-${i}`} label="—" value="—" icon={TrendingUp} loading={loading} muted />
        ))}
      </div>

      {/* === Data sources connection strip ============================== */}
      <Card className="rounded-sm border-zinc-200" data-testid="overview-data-sources">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="label-overline">Data sources</div>
              <h3 className="font-display font-bold text-xl tracking-tight">Connected integrations</h3>
              <p className="text-xs text-zinc-500 mt-1">
                Live status of every third-party data feed configured for this client.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <DataSourceTile
              testId="ds-google"
              label="Google (Ads, GA4, Search Console, YouTube)"
              icon={Globe2}
              brandColor="#002FA7"
              state={integrations.google}
              connectedHref={selected ? `/clients/${selected.client_id}/google-feeds` : null}
              setupHref={selected ? `/integrations/google` : null}
              statusText={(s) => s.loading ? "Checking…"
                : s.connected ? `${s.total} resource${s.total === 1 ? "" : "s"} mapped`
                : "Not connected"}
            />
            <DataSourceTile
              testId="ds-openai-ads"
              label="OpenAI Ads (ChatGPT)"
              icon={Sparkles}
              brandColor="#10A37F"
              state={integrations.openai_ads}
              connectedHref={selected ? `/clients/${selected.client_id}/openai-ads` : null}
              setupHref={selected ? `/clients/${selected.client_id}/openai-ads` : null}
              statusText={(s) => s.loading ? "Checking…"
                : s.connected
                  ? `${s.meta?.account_name || "Connected"}${s.meta?.currency_code ? ` · ${s.meta.currency_code}` : ""}`
                  : "Not connected"}
            />
            <DataSourceTile
              testId="ds-linkedin"
              label="LinkedIn (Ads + Pages)"
              icon={Linkedin}
              brandColor="#0A66C2"
              state={integrations.linkedin}
              connectedHref={selected ? `/clients/${selected.client_id}/linkedin` : null}
              setupHref={selected ? `/clients/${selected.client_id}/linkedin` : null}
              statusText={(s) => s.loading ? "Checking…"
                : s.connected
                  ? (s.mdp_approved
                      ? `${s.meta?.linkedin_member_name || "Connected"}`
                      : "OAuth connected · MDP approval pending")
                  : "Not connected · MDP application required"}
              warningWhenConnectedButUnapproved={
                integrations.linkedin?.connected && !integrations.linkedin?.mdp_approved
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card className="rounded-sm border-zinc-200 bg-white">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <div className="label-overline">Trend</div>
              <h3 className="font-display font-bold text-xl tracking-tight">Platform performance by month</h3>
              <p className="text-xs text-zinc-500 mt-1">
                Tap any legend chip to toggle that series. Date range controlled by the master filter above.
              </p>
            </div>
            <Link to="/charts" className="text-xs label-overline text-[#002FA7] hover:underline inline-flex items-center gap-1">
              Drill in <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          {loading ? <Skeleton className="h-72 w-full" /> :
            chartData.length === 0 ? (
              <div className="h-72 grid place-items-center text-zinc-400 text-sm">
                Upload CSV data to see trends.
              </div>
            ) : (
              <>
                <ChartLegend
                  keys={platformsInChart}
                  colors={COLORS}
                  labels={platformLabels}
                  hidden={lineToggle.hidden}
                  onToggle={lineToggle.toggle}
                  testIdPrefix="overview-chart"
                />
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={masterRange.filter(chartData)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="month" stroke="#71717A" fontSize={12} />
                    <YAxis stroke="#71717A" fontSize={12} tickFormatter={formatBig} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    {platformsInChart.map((p, i) => (
                      <Line key={p} type="monotone" dataKey={p} stroke={COLORS[i % COLORS.length]}
                            strokeWidth={2} dot={LINE_DOT} activeDot={LINE_ACTIVE_DOT}
                            hide={lineToggle.isHidden(p)} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
        </CardContent>
      </Card>

      {/* Per-property historical trends — filtered by master range */}
      <PerPropertyTrends clientId={selected.client_id} masterRange={masterRange} />

      {/* 12-month over/under budget */}
      <BudgetVarianceChart clientId={selected.client_id} />

      {/* Platform tiles */}
      <div>
        <div className="label-overline mb-3">By platform</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(data?.by_platform || {}).map(([plat, months]) => {
            // Only show data within the master range
            const inRange = (months || []).filter((m) => masterRange.inRange(m.period_month));
            const last = inRange[inRange.length - 1];
            const totals = last?.totals || {};
            const headline = Object.entries(totals)[0];
            return (
              <Card key={plat} className="rounded-sm border-zinc-200 hover:-translate-y-0.5 hover:shadow-md transition-transform">
                <CardContent className="p-5">
                  <div className="label-overline">{platformLabels[plat] || plat}</div>
                  <div className="mt-2 font-display font-extrabold text-2xl tracking-tight">
                    {headline ? formatBig(headline[1]) : last?.row_count || "—"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {headline ? headline[0] : (inRange.length
                      ? `${inRange.length} month(s) in range`
                      : "No data in range")}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {(!data?.by_platform || Object.keys(data.by_platform).length === 0) && (
            <div className="col-span-4 text-sm text-zinc-500">No platform data yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function BudgetVarianceChart({ clientId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ current_budget_total: 0, effective_hr_rate: 0 });

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/clients/${clientId}/budget-history?months=12`)
      .then(({ data }) => {
        if (cancelled) return;
        setRows(data.months || []);
        setMeta({
          current_budget_total: data.current_budget_total || 0,
          effective_hr_rate: data.effective_hr_rate || 0,
        });
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const data = (rows || []).map((r) => ({
    month: r.period_month,
    variance: r.variance == null ? 0 : Number(r.variance),
    has_report: r.has_report,
    actual_total: r.actual_total,
    budget_total: r.budget_total,
    actual_ad_spend: r.actual_ad_spend,
    actual_hr_cost: r.actual_hr_cost,
    actual_hr_hours: r.actual_hr_hours,
  }));
  const monthsWithReport = data.filter((d) => d.has_report);
  const _fmt$ = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="overview-budget-variance">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="label-overline">Budget vs actual</div>
            <h3 className="font-display font-bold text-xl tracking-tight">12-month budget variance</h3>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
              Bars above zero = <span className="text-emerald-600 font-semibold">under budget</span>,
              bars below zero = <span className="text-red-600 font-semibold">over budget</span>.
              Each month is taken from a generated report; months without a report are shown as a flat
              zero (no spend recorded yet).
            </p>
          </div>
          <div className="text-right text-xs">
            <div className="label-overline">Current budget / mo</div>
            <div className="font-display font-bold text-lg">{_fmt$(meta.current_budget_total)}</div>
            <div className="text-[11px] text-zinc-500 font-mono">
              HR rate {meta.effective_hr_rate > 0 ? `${_fmt$(meta.effective_hr_rate)}/hr` : "not set"}
            </div>
          </div>
        </div>

        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : monthsWithReport.length === 0 ? (
          <div className="h-64 grid place-items-center text-sm text-zinc-400 border border-dashed border-zinc-200 rounded-sm">
            Generate a monthly report to start tracking variance here.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data} margin={BAR_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="month" stroke="#71717A" fontSize={11} />
              <YAxis stroke="#71717A" fontSize={11} tickFormatter={_fmt$} width={70} />
              <ReferenceLine y={0} stroke="#52525B" strokeWidth={1} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE_SM}
                formatter={(value, _key, ctx) => {
                  const r = ctx?.payload || {};
                  if (!r.has_report) return ["—", "No report"];
                  return [
                    `${value >= 0 ? "Under" : "Over"} budget by ${_fmt$(Math.abs(value))}`,
                    "Variance",
                  ];
                }}
                labelFormatter={(label) => {
                  const r = data.find((x) => x.month === label) || {};
                  if (!r.has_report) return `${label} · no report`;
                  return `${label} · spent ${_fmt$(r.actual_total)} of ${_fmt$(r.budget_total)}` +
                    (r.actual_hr_hours
                      ? `  ·  ${Number(r.actual_hr_hours).toFixed(1)} hrs × HR + ${_fmt$(r.actual_ad_spend)} ads`
                      : "");
                }}
              />
              <Bar dataKey="variance" radius={BAR_RADIUS}>
                {data.map((d) => (
                  <Cell key={d.month}
                    fill={
                      !d.has_report ? "#E4E4E7"
                        : d.variance >= 0 ? "#16a34a"  // under budget — green
                        : "#dc2626"                    // over budget — red
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function KpiTile({ label, value, icon: Icon, loading, muted }) {
  return (
    <Card className={`rounded-sm border-zinc-200 ${muted ? "opacity-60" : ""}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="label-overline">{label}</div>
          <Icon className="h-4 w-4 text-zinc-400" />
        </div>
        <div className="mt-3 font-display font-extrabold text-3xl tracking-tight text-zinc-900">
          {loading ? <Skeleton className="h-8 w-20" /> : value}
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ title, body, ctaTo, ctaLabel }) {
  return (
    <div className="border border-dashed border-zinc-300 rounded-sm bg-white p-12 max-w-2xl mx-auto text-center" data-testid="empty-state">
      <div className="label-overline">Get started</div>
      <h2 className="font-display font-extrabold text-3xl mt-2 tracking-tight">{title}</h2>
      <p className="text-sm text-zinc-500 mt-3">{body}</p>
      <Link to={ctaTo} className="inline-block mt-6 px-5 py-2.5 bg-[#002FA7] text-white hover:bg-[#00227A] rounded-sm text-sm">
        {ctaLabel}
      </Link>
    </div>
  );
}

function formatBig(n) {
  const num = Number(n || 0);
  if (Math.abs(num) >= 1e9) return (num / 1e9).toFixed(1) + "B";
  if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(1) + "M";
  if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(1) + "K";
  return num % 1 === 0 ? num.toLocaleString() : num.toFixed(2);
}

function DataSourceTile({ label, icon: Icon, brandColor, state, statusText,
                         connectedHref, setupHref, testId,
                         warningWhenConnectedButUnapproved }) {
  const loading = state?.loading;
  const connected = !!state?.connected;
  // Three visual states:
  //   - loading  : neutral, skeleton-ish
  //   - connected: brand-coloured tile + green dot
  //   - pending  : amber when OAuth is connected but MDP approval is pending
  //   - notset   : muted neutral
  let badgeCls, dotCls, ringCls, badgeText, Icon2;
  if (loading) {
    badgeCls = "bg-zinc-100 text-zinc-500"; dotCls = "bg-zinc-300";
    ringCls = "border-zinc-200"; badgeText = "Checking"; Icon2 = Clock;
  } else if (warningWhenConnectedButUnapproved) {
    badgeCls = "bg-amber-100 text-amber-800"; dotCls = "bg-amber-500";
    ringCls = "border-amber-200"; badgeText = "Pending approval"; Icon2 = AlertCircle;
  } else if (connected) {
    badgeCls = "bg-emerald-100 text-emerald-800"; dotCls = "bg-emerald-500";
    ringCls = "border-zinc-200"; badgeText = "Connected"; Icon2 = CheckCircle2;
  } else {
    badgeCls = "bg-zinc-100 text-zinc-600"; dotCls = "bg-zinc-300";
    ringCls = "border-zinc-200 opacity-90"; badgeText = "Not connected"; Icon2 = AlertCircle;
  }
  const href = connected ? connectedHref : setupHref;
  const inner = (
    <div className={`rounded-sm border ${ringCls} bg-white p-4 hover:-translate-y-0.5 hover:shadow-md transition-transform`}
      data-testid={testId}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="h-8 w-8 grid place-items-center rounded-sm text-white shrink-0"
          style={{ backgroundColor: brandColor || "#71717A" }}>
          <Icon className="h-4 w-4" />
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-mono inline-flex items-center gap-1 ${badgeCls}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
          {badgeText}
        </span>
      </div>
      <div className="font-display font-bold text-sm tracking-tight leading-tight mb-1">{label}</div>
      <div className="text-[11px] text-zinc-500 font-mono flex items-center gap-1.5">
        <Icon2 className="h-3 w-3 shrink-0" />
        <span className="truncate">{statusText ? statusText(state || {}) : ""}</span>
      </div>
      {href && (
        <div className="text-[10px] uppercase tracking-wider mt-3 inline-flex items-center gap-1 text-zinc-700">
          {connected ? "Manage" : "Set up"} <ArrowUpRight className="h-3 w-3" />
        </div>
      )}
    </div>
  );
  return href ? <Link to={href} className="block">{inner}</Link> : inner;
}


export { formatBig };
